package com.trevorwiebe.timesheet.settlements.data.dto

import com.trevorwiebe.timesheet.settlements.domain.model.Settlement
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementDeduction
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementLine
import com.trevorwiebe.timesheet.settlements.domain.model.SettlementStatus
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable

@Serializable
data class SettlementDto(
    val number: Int = 0,
    val ownerOperatorId: String = "",
    val ownerOperatorName: String = "",
    val settlementDate: String = "",
    val status: String = SettlementStatus.DRAFT.name,
    val sentAt: String? = null,
    val sentTo: String = "",
    // An Int, not a Long: the web Firestore SDK can't store a Kotlin Long.
    val miles: Int = 0,
    val lines: List<SettlementLineDto> = emptyList(),
    val deductions: List<SettlementDeductionDto> = emptyList(),
    // Totals are stored so server-side code (PDF, email) needn't recompute them.
    val gross: Int = 0,
    val pay: Int = 0,
    val deductionsTotal: Int = 0,
    val netPay: Int = 0,
    val pdfPath: String = "",
    val carrierId: String = "",
)

@Serializable
data class SettlementLineDto(
    val loadId: String = "",
    val loadNumber: String = "",
    val date: String = "",
    val driverId: String = "",
    val driverName: String = "",
    val pickup: String = "",
    val drop: String = "",
    val base: Int = 0,
    val fuelSurcharge: Int = 0,
    val detention: Int = 0,
    val stopOff: Int = 0,
    val other: Int = 0,
    val basePayPercent: Double = 0.0,
    // Older settlements paid every extra charge at this one percentage.
    val extraPayPercent: Double = 100.0,
    val fuelSurchargePayPercent: Double? = null,
    val detentionPayPercent: Double? = null,
    val stopOffPayPercent: Double? = null,
    val otherPayPercent: Double? = null,
    val gross: Int = 0,
    val pay: Int = 0,
    val unitDetail: String = "",
)

@Serializable
data class SettlementDeductionDto(val description: String = "", val amount: Int = 0)

/** One document per settled load, keyed by load id; lets a transaction refuse paying a load twice. */
@Serializable
data class SettledLoadDto(val settlementId: String = "")

fun SettlementDto.toSettlement(id: String): Settlement = Settlement(
    id = id,
    number = number,
    ownerOperatorId = ownerOperatorId,
    ownerOperatorName = ownerOperatorName,
    settlementDate = LocalDate.parse(settlementDate),
    lines = lines.map {
        SettlementLine(
            loadId = it.loadId,
            loadNumber = it.loadNumber,
            date = LocalDate.parse(it.date),
            driverId = it.driverId,
            driverName = it.driverName,
            pickup = it.pickup,
            drop = it.drop,
            base = it.base,
            fuelSurcharge = it.fuelSurcharge,
            detention = it.detention,
            stopOff = it.stopOff,
            other = it.other,
            basePayPercent = it.basePayPercent,
            fuelSurchargePayPercent = it.fuelSurchargePayPercent ?: it.extraPayPercent,
            detentionPayPercent = it.detentionPayPercent ?: it.extraPayPercent,
            stopOffPayPercent = it.stopOffPayPercent ?: it.extraPayPercent,
            otherPayPercent = it.otherPayPercent ?: it.extraPayPercent,
            unitDetail = it.unitDetail,
        )
    },
    deductions = deductions.map { SettlementDeduction(it.description, it.amount) },
    miles = miles.toLong(),
    status = SettlementStatus.entries.firstOrNull { it.name == status } ?: SettlementStatus.DRAFT,
    sentAt = sentAt?.let { LocalDate.parse(it) },
    sentTo = sentTo,
    pdfPath = pdfPath,
    carrierId = carrierId,
)

fun Settlement.toSettlementDto(): SettlementDto = SettlementDto(
    number = number,
    ownerOperatorId = ownerOperatorId,
    ownerOperatorName = ownerOperatorName,
    settlementDate = settlementDate.toString(),
    status = status.name,
    sentAt = sentAt?.toString(),
    sentTo = sentTo,
    miles = miles.toInt(),
    lines = lines.map {
        SettlementLineDto(
            loadId = it.loadId,
            loadNumber = it.loadNumber,
            date = it.date.toString(),
            driverId = it.driverId,
            driverName = it.driverName,
            pickup = it.pickup,
            drop = it.drop,
            base = it.base,
            fuelSurcharge = it.fuelSurcharge,
            detention = it.detention,
            stopOff = it.stopOff,
            other = it.other,
            basePayPercent = it.basePayPercent,
            fuelSurchargePayPercent = it.fuelSurchargePayPercent,
            detentionPayPercent = it.detentionPayPercent,
            stopOffPayPercent = it.stopOffPayPercent,
            otherPayPercent = it.otherPayPercent,
            gross = it.gross,
            pay = it.pay,
            unitDetail = it.unitDetail,
        )
    },
    deductions = deductions.map { SettlementDeductionDto(it.description, it.amount) },
    gross = gross,
    pay = pay,
    deductionsTotal = deductionsTotal,
    netPay = netPay,
    pdfPath = pdfPath,
    carrierId = carrierId,
)

@Serializable
data class SettlementIdRequest(val settlementId: String)

@Serializable
data class SendSettlementRequest(val settlementId: String, val to: String)

@Serializable
data class SettlementPdfResponse(val pdfPath: String = "")

/** [sent] is false when the server skipped the email (it never emails from the emulator). */
@Serializable
data class SendSettlementResponse(val sent: Boolean = false)
